import { useNavigate, useLocation } from 'react-router-dom';
import { Map, ImagePlus, BarChart3, Share2, User, LogOut } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const NAV_ITEMS = [
  { path: '/map', icon: Map, label: '地图' },
  { path: '/import', icon: ImagePlus, label: '导入' },
  { path: '/stats', icon: BarChart3, label: '统计' },
  { path: '/poster', icon: Share2, label: '海报' },
] as const;

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const displayName = user
    ? (user.is_anonymous ? '访客' : (user.user_metadata?.display_name || user.email?.split('@')[0] || '用户'))
    : '';

  return (
    <>
      {/* Desktop Navbar */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-[1000] glass border-b border-dark-border">
        <div className="flex items-center justify-between px-6 py-3 w-full">
          {/* Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={() => navigate('/map')}
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-cyan to-blue-600 flex items-center justify-center shadow-lg shadow-neon-cyan/20 group-hover:shadow-neon-cyan/40 transition-shadow">
              <Map className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-neon-cyan to-neon-blue bg-clip-text text-transparent">
              TripMap
            </span>
          </div>

          {/* Nav links */}
          <div className="flex items-center gap-1">
            {NAV_ITEMS.map(({ path, icon: Icon, label }) => (
              <NavButton
                key={path}
                icon={<Icon className="w-4 h-4" />}
                label={label}
                active={isActive(path)}
                onClick={() => navigate(path)}
              />
            ))}
          </div>

          {/* User */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <div className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                  onClick={() => navigate('/stats')}
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-neon-cyan to-blue-600 flex items-center justify-center shadow-md shadow-neon-cyan/20">
                    <User className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-gray-300 max-w-[120px] truncate">
                    {displayName}
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                  title="退出登录"
                >
                  <LogOut className="w-4 h-4 text-gray-400 hover:text-red-400 transition-colors" />
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-1.5 rounded-lg bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan text-sm hover:bg-neon-cyan/20 transition-colors"
              >
                登录
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar (compact) */}
      <div className="flex md:hidden fixed top-0 left-0 right-0 z-[1000] glass border-b border-dark-border">
        <div className="flex items-center justify-between px-4 py-2 w-full">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/map')}>
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-neon-cyan to-blue-600 flex items-center justify-center">
              <Map className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-neon-cyan to-neon-blue bg-clip-text text-transparent">
              TripMap
            </span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-neon-cyan to-blue-600 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <button onClick={handleSignOut} className="p-1.5 rounded-lg hover:bg-white/5" title="退出">
                  <LogOut className="w-4 h-4 text-gray-400" />
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-3 py-1 rounded-lg bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan text-sm"
              >
                登录
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <div className="flex md:hidden fixed bottom-0 left-0 right-0 z-[1000] glass border-t border-dark-border">
        <div className="flex items-center justify-around w-full px-2 py-1 safe-area-bottom">
          {NAV_ITEMS.map(({ path, icon: Icon, label }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex flex-col items-center gap-0.5 py-2 px-3 rounded-xl transition-all min-h-0 min-w-0 ${
                  active
                    ? 'text-neon-cyan'
                    : 'text-gray-500 active:text-gray-300'
                }`}
              >
                <div className={`relative ${active ? '' : ''}`}>
                  <Icon className="w-5 h-5" />
                  {active && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-neon-cyan shadow-[0_0_6px_rgba(0,245,255,0.6)]" />
                  )}
                </div>
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
      active
        ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/20 shadow-sm shadow-neon-cyan/10'
        : 'text-gray-400 hover:text-white hover:bg-white/5'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);
